import React, {
    FormEvent, ReactElement, useEffect, useState,
} from "react";
import { Form } from "react-bootstrap";
import { AiOutlinePlus } from "react-icons/ai";
import { BsTrash } from "react-icons/bs";
import { GoVerified } from "react-icons/go";
import { VscSettingsGear } from "react-icons/vsc";
import Confirm from "../../../../../components/confirm/Confirm";
import Editor from "../../../../../components/editor/Editor";
import TimedMessage from "../../../../../components/timed-message/TimedMessage";
import WorkflowPicker from "../../../../../components/workflow/WorkflowPicker";
import {
    GetChannelEmailsDocument,
    GetSettingsDocument, useAddEmailAliasMutation, useDeleteEmailAliasMutation, useSaveSettingsMutation,
} from "../../../../../__generated__/api";
import { SettingsProps } from "../../base";
import useArticleContext from "../../../../../contexts/HelpContext";
import useDirtyTracker from "../../../../../components/dirty-tracker/DirtyTracker";
import { NavLink, Paths } from "../../../../../contexts/Routing";

export default function EmailSettings(props: SettingsProps): ReactElement {
    useArticleContext("urn:fq:article:aec1306fe20e498590f512e76c2cf74e");

    const [save] = useAddEmailAliasMutation();
    const [saveFooter] = useSaveSettingsMutation();
    const [deleteAlias] = useDeleteEmailAliasMutation();

    const [footer, setFooter] = useState(props.settings?.channelEmail?.footer);

    const [emails, setEmails] = useState(props.settings?.channelEmail?.emails ?? []);
    const [showCreateAlias, setShowCreateAlias] = useState(false);
    const [isSubmitted, setSubmitted] = useState(false);

    const [deleteEmailId, setDeleteEmailId] = useState<string | undefined>("");
    const [deleteEmailText, setDeleteEmailText] = useState<string | undefined>("");

    const [addingEmail, setAddingEmail] = useState("");

    useEffect(() => {
        setEmails(props.settings?.channelEmail?.emails ?? []);
    }, [props.settings]);

    const onSubmit = (event: FormEvent) => {
        event.preventDefault();
        (async () => {
            await save({
                refetchQueries: [
                    { query: GetSettingsDocument },
                    { query: GetChannelEmailsDocument },
                ],

                variables: {
                    email: addingEmail,
                },
            });
        })();
        setSubmitted(true);
        setShowCreateAlias(false);
    };

    useEffect(() => {
        if (isSubmitted) {
            setTimeout(() => setSubmitted(false), 5000);
        }
    }, [isSubmitted]);

    const onDelete = () => {
        (async () => {
            await deleteAlias({
                refetchQueries: [
                    { query: GetSettingsDocument },
                    { query: GetChannelEmailsDocument },
                ],
                variables: {
                    id: deleteEmailId,
                },
            });
            setDeleteEmailId("");
        })();
    };

    const startDelete = (id: string, text: string) => {
        setDeleteEmailId(id);
        setDeleteEmailText(text);
    };
    const defaultEmail = emails.find(email => email.isDefault);
    const nonDefault = emails;// .filter(email => !email.isDefault);

    const dirtyTracker = useDirtyTracker({
        finishedSavingMessage: "Email settings saved!",
        onSave: (event, onDone) => {
            event.preventDefault();
            (async () => {
                await saveFooter({
                    variables: {
                        channelEmail: {
                            footer,
                        },
                    },
                });
                onDone();
            })();
        },
    });

    dirtyTracker.useWatchProperty(footer, props.settings?.channelEmail?.footer);

    dirtyTracker.useLoad(() => {
        setFooter(props.settings?.channelEmail?.footer);
    }, [props.settings?.channels?.channelTags]);

    return (
        <div className="d-flex align-items-stretch flex-column">
            <Confirm
                confirmText="Delete"
                onConfirm={onDelete}
                shouldShow={deleteEmailId !== ""}
                onHide={() => setDeleteEmailId("")}
                confirmVariant="danger"
                confirmActionText="delete"
            >
                <div>
                    Are you sure you want to delete email alias:
                    {" "}
                    <div className="mt-2 p-2 rounded alert alert-danger">
                        {deleteEmailText}
                    </div>

                </div>
            </Confirm>

            <TimedMessage
                shouldShow={isSubmitted}
                variant="primary"
                seconds={10}
                title="Email Alias added"
                onClose={() => {
                    setAddingEmail("");
                    setSubmitted(false);
                }}
            >
                <div>
                    Your email was submitted. Please check your email and look for a validation email from us.
                    Once you receive the email and validate it, you will be able to send emails on behalf
                    of the provided alias.
                    <pre className="pt-3">
                        <code>
                            {addingEmail}
                        </code>
                    </pre>

                </div>
            </TimedMessage>

            <div className="border p-3 rounded bg-white mb-4">
                <div>
                    Your forwarding address:
                    <pre className="mt-3 ps-3 alert alert-info">
                        <code>
                            {defaultEmail?.originalDestinationEmail}
                        </code>
                    </pre>
                    <small>
                        This is an automatically generated email for your email domain.
                        When an email is sent to
                        {" "}
                        {defaultEmail?.originalDestinationEmail}
                        , the email will be automatically picked up by Agent and you will be notified.
                        If you want to setup your email Alias (for example support@example.com),
                        create a new email alias below and validate it.
                    </small>
                </div>
            </div>
            <form className="border p-3 rounded bg-white mb-4">
                <h5 className="mb-3 text-light">
                    Settings
                </h5>
                <Form.Group className="mt-4">
                    <Form.Label className="fw-bolder">Footer</Form.Label>

                    <Editor
                        className="border border-gray rounded"
                        text={footer ?? ""}
                        onChange={msg => setFooter(msg)}
                        placeholder="Powered by Foqal"
                    />
                    <Form.Text>
                        This message will automatically be added to the bottom of each email
                    </Form.Text>
                </Form.Group>

                {dirtyTracker.saveComponent}

            </form>

            {showCreateAlias && (
                <form className="flex-grow-1" onSubmit={onSubmit}>

                    <div className="border p-3 rounded mb-4 bg-white">
                        <h5 className="mb-3 text-light">
                            New Email Alias
                        </h5>
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bolder">Email</Form.Label>
                            <Form.Control
                                type="email"
                                placeholder="support@example.com"
                                required
                                onChange={evt => setAddingEmail(evt.target.value)}
                                value={addingEmail as string}
                            />
                            <Form.Text>
                                The email address you want to setup as a alias. Once registered, validated, and
                                after email forwarding is configured, any email sent to the one above will forward to
                                Agent and notify your team.
                            </Form.Text>
                        </Form.Group>

                        <div className="d-flex justify-content-end mt-3 bg-white">
                            <button type="button" className="btn btn-secondary me-2" onClick={() => setShowCreateAlias(false)}>
                                Cancel
                            </button>
                            <button type="submit" className="btn btn-primary">
                                Add
                            </button>
                        </div>

                    </div>

                </form>
            )}

            {!showCreateAlias && nonDefault.isEmpty && (
                <div className="border p-3 rounded bg-white mb-3">
                    <div className="d-flex flex-column align-items-center justify-content-center h-100 text-light my-4">
                        <h5 className="text-light">
                            Email Alias
                        </h5>
                        <button
                            type="button"
                            className="btn btn-secondary btn-sm my-3"
                            onClick={() => setShowCreateAlias(true)}
                        >
                            <AiOutlinePlus />
                            {" "}
                            Add Email Alias
                        </button>
                        <small>
                            You may want to use your branded email (like support@example.com)
                            to send and receive email with Agent. To set this up, create an email
                            alias you would like to publicize, add it to Agent Settings.
                        </small>
                    </div>
                </div>

            )}
            {nonDefault.isNotEmpty && (
                <div className="border p-3 rounded bg-white mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="text-light">
                            Email Aliases
                        </h5>
                        {!showCreateAlias && (
                            <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                onClick={() => setShowCreateAlias(true)}
                            >
                                <AiOutlinePlus />
                                {" "}
                                Email Alias
                            </button>
                        )}

                    </div>

                    <div className="list-group">
                        {nonDefault.sortBy(email => email.originalDestinationEmail?.toLowerCase()).map(email => (
                            <div
                                key={email.id}
                                className={"list-group-item list-group-item-action "
                                        + "d-flex align-items-center d-flex justify-content-between"}
                            >
                                <div className="flex-grow-1">
                                    <h5 className="mb-0">
                                        {email.originalDestinationEmail}
                                        {" "}
                                        {email.verifiedDate == null ? <GoVerified className="text-success" /> : ""}

                                    </h5>
                                    <small>
                                        {email.isDefault ? "(Default)" : ""}
                                    </small>
                                </div>
                                <div className="d-flex flex-grow-0 align-items-end">

                                    <NavLink
                                        to={Paths.ADMINISTRATOR_SETTING_ID}
                                        params={{
                                            id: email.id,
                                            setting: "chat",
                                        }}
                                        className="btn btn-none"
                                    >
                                        <VscSettingsGear className="text-light" />
                                    </NavLink>
                                    {!email.isDefault && (
                                        <>
                                            <WorkflowPicker
                                                buttonClassName="text-light"
                                                iconClassName="text-light"
                                                referenceId={email.id}
                                                title={`Workflow for ${email.originalDestinationEmail}`}
                                            />

                                            <button
                                                type="button"
                                                aria-label="Delete Email alias"
                                                className="btn btn-none"
                                                onClick={() => startDelete(email.id, email.originalDestinationEmail as string)}
                                            >
                                                <BsTrash className="text-light" />
                                            </button>
                                        </>
                                    )}

                                </div>

                            </div>

                        ))}

                    </div>

                </div>
            )}

        </div>

    );
}
